import type { CardAccount } from '@bloque/sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { toast } from '@workspace/ui/components/sonner';
import {
  ArrowDownToLine,
  ArrowRightLeft,
  ExternalLink,
  MoreHorizontal,
  Pencil,
  Play,
  ShieldOff,
  Snowflake,
} from 'lucide-react';
import { useState } from 'react';
import { bloque } from '~/libs/bloque';
import { m } from '~/paraglide/messages.js';
import type { Card } from '../../-types/card';

interface ActionBarProps {
  card: Card;
  cardUrn: string;
  isBalanceLoading: boolean;
  hasBalance: boolean;
  isVerified: boolean;
  openKycModal: () => void;
  onTopUp: () => void;
  onTransfer: () => void;
  onEditName: () => void;
}

export function ActionBar({
  card,
  cardUrn,
  isBalanceLoading,
  hasBalance,
  isVerified,
  openKycModal,
  onTopUp,
  onTransfer,
  onEditName,
}: ActionBarProps) {
  const queryClient = useQueryClient();
  const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [viewingData, setViewingData] = useState(false);

  const status = (card.status ?? card.details?.card_status ?? '').toLowerCase();
  const isFrozen = status === 'frozen' || status === 'suspended';
  const isDisabled = status === 'disabled' || status === 'inactive';
  const isNotActive = isFrozen || isDisabled;

  const invalidateCardQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    queryClient.invalidateQueries({ queryKey: ['account', card.urn] });
    queryClient.invalidateQueries({ queryKey: ['account-sdk', card.urn] });
  };

  const freezeMutation = useMutation({
    mutationFn: () => bloque.accounts.card.freeze(card.urn),
    onSuccess: () => {
      toast.success(m['cards.controls.freeze']());
      invalidateCardQueries();
      setFreezeDialogOpen(false);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to freeze card'),
  });

  const activateMutation = useMutation({
    mutationFn: () => bloque.accounts.card.activate(card.urn),
    onSuccess: () => {
      toast.success(
        isFrozen
          ? m['cards.controls.unfreeze']()
          : m['cards.controls.activate'](),
      );
      invalidateCardQueries();
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : 'Failed to activate card',
      ),
  });

  const disableMutation = useMutation({
    mutationFn: () => bloque.accounts.card.disable(card.urn),
    onSuccess: () => {
      toast.success(m['cards.controls.disable']());
      invalidateCardQueries();
      setDisableDialogOpen(false);
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : 'Failed to disable card',
      ),
  });

  const handleViewCardData = async () => {
    if (!cardUrn) {
      toast.error('Account URN is required');
      return;
    }
    setViewingData(true);
    try {
      const freshCard: unknown = await bloque.accounts.get(cardUrn);
      const cardAccount = freshCard as CardAccount;
      const url = cardAccount.detailsUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Card data URL not available');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to fetch card data',
      );
    } finally {
      setViewingData(false);
    }
  };

  const kycGuard = (fn: () => void) => () => {
    if (!isVerified) {
      openKycModal();
      return;
    }
    fn();
  };

  const actions = [
    {
      icon: ArrowDownToLine,
      label: m['cards.detail.pse_topup'](),
      onClick: kycGuard(onTopUp),
      disabled: isBalanceLoading,
    },
    {
      icon: ArrowRightLeft,
      label: m['cards.detail.transfer'](),
      onClick: kycGuard(onTransfer),
      disabled: isBalanceLoading || !hasBalance,
    },
    {
      icon: isNotActive ? Play : Snowflake,
      label: isFrozen
        ? m['cards.controls.unfreeze']()
        : isDisabled
          ? m['cards.controls.activate']()
          : m['cards.controls.freeze'](),
      onClick: isNotActive
        ? () => activateMutation.mutate()
        : () => setFreezeDialogOpen(true),
      disabled: freezeMutation.isPending || activateMutation.isPending,
    },
  ];

  return (
    <>
      <div className="flex items-center justify-center gap-6">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className="group flex flex-col items-center gap-1.5 disabled:opacity-40"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-purple-500/20 bg-purple-500/5 transition-colors group-hover:bg-purple-500/15 group-hover:border-purple-500/40">
              <action.icon className="h-5 w-5 text-purple-300" />
            </div>
            <span className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
              {action.label}
            </span>
          </button>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-col items-center gap-1.5"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-purple-500/20 bg-purple-500/5 transition-colors group-hover:bg-purple-500/15 group-hover:border-purple-500/40">
                <MoreHorizontal className="h-5 w-5 text-purple-300" />
              </div>
              <span className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
                {m['common.actions']()}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleViewCardData}
              disabled={viewingData}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {m['cards.controls.view_card_data']()}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEditName}>
              <Pencil className="mr-2 h-4 w-4" />
              {m['cards.controls.edit_name']()}
            </DropdownMenuItem>
            {!isDisabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDisableDialogOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <ShieldOff className="mr-2 h-4 w-4" />
                  {m['cards.controls.disable']()}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={freezeDialogOpen} onOpenChange={setFreezeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {m['cards.controls.freeze_confirm_title']()}
            </DialogTitle>
            <DialogDescription>
              {m['cards.controls.freeze_confirm_description']()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFreezeDialogOpen(false)}
            >
              {m['common.cancel']()}
            </Button>
            <Button
              onClick={() => freezeMutation.mutate()}
              disabled={freezeMutation.isPending}
            >
              {freezeMutation.isPending
                ? m['cards.controls.freezing']()
                : m['cards.controls.freeze']()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {m['cards.controls.disable_confirm_title']()}
            </DialogTitle>
            <DialogDescription>
              {m['cards.controls.disable_confirm_description']()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisableDialogOpen(false)}
            >
              {m['common.cancel']()}
            </Button>
            <Button
              variant="destructive"
              onClick={() => disableMutation.mutate()}
              disabled={disableMutation.isPending}
            >
              {m['cards.controls.disable']()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
